import type {
    CommerceAddress,
    CommerceCheckout,
    CommerceCheckoutItem,
    CommerceProductComponent,
    CommerceCheckoutValidationMessage,
    CommerceShippingMethod,
} from "@teqtank/commerce-contracts";
import { resolveRecurringFrequencyOption } from "../products/product-response-resolver.js";
import { CommerceError } from "../errors/commerce-error.js";
import { resolveCommerceCart } from "../cart/cart-response-resolver.js";

export function resolveCommerceCheckout(value: CommerceCheckout): CommerceCheckout {
    if (
        value === null ||
        typeof value !== "object" ||
        typeof value.id !== "string" ||
        !Number.isInteger(value.version)
    ) {
        throw new CommerceError(
            "checkout-response-invalid",
            "The Commerce Gateway returned an invalid Checkout response.",
        );
    }

    const shippingAddress = resolveAddress(value.shippingAddress);
    const billingAddress = resolveAddress(value.billingAddress);
    const contact =
        value.contact === null
            ? null
            : Object.freeze({
                  email: value.contact.email,
                  phone: value.contact.phone,
                  acceptsMarketing: value.contact.acceptsMarketing,
              });
    const shippingMethods = Object.freeze(value.shippingMethods.map(resolveShippingMethod));
    const items = Object.freeze(value.items.map(resolveCheckoutItem));
    const upsells = Object.freeze(
        (value.upsells ?? []).map((upsell) =>
            Object.freeze({
                ...upsell,
                image: upsell.image === null ? null : Object.freeze({ ...upsell.image }),
                choices: Object.freeze(
                    upsell.choices.map((choice) => {
                        const {
                            frequency: rawFrequency,
                            saving: rawSaving,
                            ...choiceValues
                        } = choice;

                        return Object.freeze({
                            ...choiceValues,
                            ...(rawFrequency == null
                                ? {}
                                : {
                                      frequency: resolveRecurringFrequencyOption(
                                          rawFrequency,
                                          "checkoutUpsellChoice.frequency",
                                      ),
                                  }),
                            price: Object.freeze({ ...choice.price }),
                            ...(rawSaving == null
                                ? {}
                                : { saving: Object.freeze({ ...rawSaving }) }),
                        });
                    }),
                ),
            }),
        ),
    );
    const errors = Object.freeze(value.validation.errors.map(resolveValidationMessage));
    const warnings = Object.freeze(value.validation.warnings.map(resolveValidationMessage));
    const experience = value.experience ?? "shop";
    if (experience !== "shop" && experience !== "enrollment") {
        throw new CommerceError(
            "checkout-response-invalid",
            "The Commerce Gateway returned an unsupported Checkout experience.",
        );
    }

    return Object.freeze({
        id: value.id,
        version: value.version,
        status: value.status,
        experience,
        cart: resolveCommerceCart(value.cart),
        contact,
        shippingAddress,
        billingAddress,
        billingAddressSameAsShipping: value.billingAddressSameAsShipping,
        shippingMethods,
        selectedShippingMethodId: value.selectedShippingMethodId,
        selectedSavedPaymentMethodId: value.selectedSavedPaymentMethodId ?? null,
        items,
        upsells,
        totals: Object.freeze({
            subtotal: Object.freeze({ ...value.totals.subtotal }),
            discount: Object.freeze({ ...value.totals.discount }),
            shipping: Object.freeze({ ...value.totals.shipping }),
            itemTax: Object.freeze({ ...value.totals.itemTax }),
            shippingTax: Object.freeze({ ...value.totals.shippingTax }),
            tax: Object.freeze({ ...value.totals.tax }),
            total: Object.freeze({ ...value.totals.total }),
        }),
        validation: Object.freeze({
            isValid: value.validation.isValid,
            hasErrors: value.validation.hasErrors,
            errors,
            warnings,
            ...(value.validation.suggestedAddress == null
                ? {}
                : {
                      suggestedAddress: resolveAddress(value.validation.suggestedAddress)!,
                  }),
        }),
        updatedAt: new Date(value.updatedAt),
    });
}

function resolveAddress(value: CommerceAddress | null): CommerceAddress | null {
    return value === null
        ? null
        : Object.freeze({
              firstName: value.firstName,
              lastName: value.lastName,
              ...(value.company != null ? { company: value.company } : {}),
              addressLine1: value.addressLine1,
              ...(value.addressLine2 != null ? { addressLine2: value.addressLine2 } : {}),
              city: value.city,
              region: value.region,
              postalCode: value.postalCode,
              countryCode: value.countryCode,
              ...(value.phone == null ? {} : { phone: value.phone }),
          });
}

function resolveShippingMethod(value: CommerceShippingMethod): CommerceShippingMethod {
    return Object.freeze({
        ...value,
        price: Object.freeze({ ...value.price }),
    });
}

function resolveCheckoutItem(value: CommerceCheckoutItem): CommerceCheckoutItem {
    return Object.freeze({
        id: value.id,
        productId: value.productId,
        variantId: value.variantId,
        ...(value.displaySku == null ? {} : { displaySku: value.displaySku }),
        name: value.name,
        image: value.image === null ? null : Object.freeze({ ...value.image }),
        purchaseType: value.purchaseType,
        ...(value.frequency == null
            ? {}
            : {
                  frequency: resolveRecurringFrequencyOption(
                      value.frequency,
                      "checkoutItem.frequency",
                  ),
              }),
        ...(value.frequencyDays == null ? {} : { frequencyDays: value.frequencyDays }),
        quantity: value.quantity,
        unitPrice: Object.freeze({ ...value.unitPrice }),
        lineSubtotal: Object.freeze({ ...value.lineSubtotal }),
        lineDiscount: Object.freeze({ ...value.lineDiscount }),
        lineTotal: Object.freeze({ ...value.lineTotal }),
        source: value.source,
        ...(value.promotionCode != null ? { promotionCode: value.promotionCode } : {}),
        ...(value.purchaseTypeLocked === undefined
            ? {}
            : { purchaseTypeLocked: value.purchaseTypeLocked }),
        ...(value.purchaseTypeLockReason == null
            ? {}
            : { purchaseTypeLockReason: value.purchaseTypeLockReason }),
        ...(value.purchaseOptions == null
            ? {}
            : {
                  purchaseOptions: Object.freeze(
                      value.purchaseOptions.map((offer) =>
                          Object.freeze({
                              purchaseType: offer.purchaseType,
                              price: Object.freeze({
                                  current: Object.freeze({ ...offer.price.current }),
                                  ...(offer.price.compareAt == null
                                      ? {}
                                      : {
                                            compareAt: Object.freeze({
                                                ...offer.price.compareAt,
                                            }),
                                        }),
                              }),
                              ...(offer.frequency == null
                                  ? {}
                                  : {
                                        frequency: Object.freeze({
                                            allowedDays: Object.freeze([
                                                ...offer.frequency.allowedDays,
                                            ]),
                                            defaultDays: offer.frequency.defaultDays,
                                            options: Object.freeze(
                                                offer.frequency.options.map((option) =>
                                                    resolveRecurringFrequencyOption(
                                                        option,
                                                        "checkoutItem.purchaseOptions.frequency",
                                                    ),
                                                ),
                                            ),
                                            defaultOptionId: offer.frequency.defaultOptionId,
                                        }),
                                    }),
                          }),
                      ),
                  ),
              }),
        ...(value.components == null
            ? {}
            : { components: Object.freeze(value.components.map(resolveCheckoutComponent)) }),
    });
}

function resolveCheckoutComponent(
    component: NonNullable<CommerceCheckoutItem["components"]>[number],
): CommerceProductComponent {
    return Object.freeze({
        ...component,
        image: component.image === null ? null : Object.freeze({ ...component.image }),
        ...(component.components == null
            ? {}
            : { components: Object.freeze(component.components.map(resolveCheckoutComponent)) }),
    });
}

function resolveValidationMessage(
    value: CommerceCheckoutValidationMessage,
): CommerceCheckoutValidationMessage {
    return Object.freeze({
        code: value.code,
        message: value.message,
        ...(value.field == null ? {} : { field: value.field }),
        severity: value.severity,
    });
}
